
import { useEffect, ReactNode } from "react";
import AuthContext from "@/hooks/useAuth";
import { useAuthState } from "@/hooks/useAuthState";
import { useActivityTracking } from "@/hooks/useActivityTracking";
import { useAuthEventHandlers } from "@/hooks/useAuthEventHandlers";
import { useSessionManagement } from "@/hooks/useSessionManagement";
import { useSecureLogin } from "@/hooks/useSecureLogin";
import { toast } from "sonner";

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  // Initialize auth state
  const authState = useAuthState();
  const { isMounted } = authState;
  
  // Get session management functions
  const { signOut, refreshSession } = useSessionManagement(authState);
  
  // Set up authentication event handlers
  useAuthEventHandlers(authState, signOut);
  
  // Get secure login function
  const { secureLogin } = useSecureLogin(authState);
  
  // Track component mounted state to prevent state updates after unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
      
      // Clear any pending timeouts
      if (authState.authTimeoutRef.current) {
        clearTimeout(authState.authTimeoutRef.current);
        authState.authTimeoutRef.current = null;
      }
      
      // Clear session check interval
      if (authState.sessionCheckIntervalRef.current) {
        clearInterval(authState.sessionCheckIntervalRef.current);
        authState.sessionCheckIntervalRef.current = null;
      }
    };
  }, [authState]);

  return (
    <AuthContext.Provider value={{ 
      session: authState.session, 
      user: authState.user, 
      isLoading: authState.isLoading, 
      signOut, 
      refreshSession,
      secureLogin
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export { useAuth } from "@/hooks/useAuth";
